import { NodeTagsMap } from "@features/administration/products/EditProduct/nodeTagsHelper"
import { ValidationState } from "@features/builder/graph/nodes/validation/types"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import { getTagErrors } from "@features/builder/NodeDrawer/NodeForm/helpers"
import { Delete } from "@mui/icons-material"
import {
  Autocomplete,
  FormHelperText,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material"
import { useMemo } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"

type TagsFieldRowProps = {
  index: number
  onDelete: () => void
  tagsMap: NodeTagsMap
  validationErrors: ValidationState["errors"]
}

export const TagsFieldRow = (props: TagsFieldRowProps) => {
  const form = useFormContext<NodeFormValues>()
  const tagsValue = useWatch({ control: form.control, name: "tags" }) ?? []

  const currentlySelectedTag = tagsValue[props.index]?.tag ?? ""
  const currentlySelectedTagType = props.tagsMap.get(currentlySelectedTag)?.type ?? "text"
  const rowValidationErrors = getTagErrors(props.validationErrors, currentlySelectedTag)

  const tagsOptions = Array.from(props.tagsMap.keys())
  const valuesOptions = useMemo(() => {
    const options = props.tagsMap.get(currentlySelectedTag)?.options ?? []
    // add extra "searchText" property to allow searching by both value and description
    return options.map((option) => ({
      ...option,
      searchText: `${option.value} ${option.description}`.toLowerCase(),
    }))
  }, [currentlySelectedTag, props.tagsMap])

  return (
    <Grid container item spacing={1}>
      <Grid item xs>
        <Controller
          control={form.control}
          name={`tags.${props.index}.tag`}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              onChange={(_, value) => field.onChange(value)}
              disableClearable
              autoHighlight
              autoSelect
              size="small"
              // disable already used tags to avoid defining the same tag multiple times
              getOptionDisabled={(option) => tagsValue.some(({ tag }) => tag === option)}
              options={tagsOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name={field.name}
                  error={!!fieldState.error?.message}
                  helperText={fieldState.error?.message}
                  label="Tag"
                  InputLabelProps={{ shrink: true, ...params.InputLabelProps }}
                />
              )}
            />
          )}
        />
      </Grid>
      <Grid item xs>
        <Controller
          control={form.control}
          name={`tags.${props.index}.value`}
          render={({ field, fieldState }) => {
            const existingOption = valuesOptions.find((o) => o.value === field.value)
            // option may no longer exist, but it is already saved on the node (still need to show it)
            const fallbackOption = { value: field.value ?? "", description: "", searchText: "" }
            const selectedOption = existingOption || fallbackOption

            return currentlySelectedTagType === "dropdown" ? (
              <Autocomplete
                options={valuesOptions}
                getOptionLabel={(option) => option.value}
                filterOptions={(options, state) =>
                  options.filter((option) =>
                    option.searchText.includes(state.inputValue.toLowerCase()),
                  )
                }
                renderOption={(propsOption, option) => (
                  <ListItem {...propsOption}>
                    <ListItemText primary={option.value} secondary={option.description} />
                  </ListItem>
                )}
                value={selectedOption}
                onChange={(_, value) => field.onChange(value?.value ?? "")}
                disableClearable
                disabled={field.disabled || !tagsValue[props.index]?.tag}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={field.name}
                    error={!!fieldState.error?.message}
                    helperText={fieldState.error?.message}
                    label="Value"
                    InputLabelProps={{ shrink: true, ...params.InputLabelProps }}
                  />
                )}
              />
            ) : (
              <TextField
                {...field}
                size="small"
                fullWidth
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
                label="Value"
                InputLabelProps={{ shrink: true }}
              />
            )
          }}
        />
        {rowValidationErrors.map((error, index) => (
          <FormHelperText key={index} error>
            - {error.msg}
          </FormHelperText>
        ))}
      </Grid>
      <Grid item xs={1}>
        <Tooltip title="Delete">
          <span>
            <IconButton
              color="error"
              size="small"
              disabled={form.formState.disabled}
              onClick={props.onDelete}
            >
              <Delete />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  )
}
